import { mcGetItem, mcSetItem, mcSetCloudItem } from "./mcCloudSync";
import { RECIPIENT_ROLE_OPTIONS } from "./mcResponseRecipients";
import { loadSites, normalizeSiteId } from "./mcOrgData";
import { loadProfiles } from "./profileAccess";

export const MC_NOTIFICATIONS_KEY = "mc-response-notifications";
export const MC_NOTIFICATION_RULES_KEY = "mc-response-notification-rules";
export const MC_NOTIFICATION_EVENT = "mc-response-notification-event";

const MAX_NOTIFICATIONS = 2500;
const DEFAULT_RULES = [
  {
    id: "rule-critical-immediate",
    name: "Critical variance immediate alert",
    active: true,
    trigger: "severity",
    severities: ["critical"],
    minExposure: 0,
    minFlaggedProducts: 0,
    recipients: ["Section Owner", "Manager"],
    channels: ["workspace", "email"],
    deliveryMode: "immediate",
  },
  {
    id: "rule-high-workspace-email",
    name: "High variance alert",
    active: true,
    trigger: "severity",
    severities: ["high"],
    minExposure: 0,
    minFlaggedProducts: 0,
    recipients: ["Manager"],
    channels: ["workspace", "email"],
    deliveryMode: "immediate",
  },
  {
    id: "rule-medium-workspace",
    name: "Medium variance workspace alert",
    active: true,
    trigger: "severity",
    severities: ["medium"],
    minExposure: 0,
    minFlaggedProducts: 0,
    recipients: ["Manager"],
    channels: ["workspace"],
    deliveryMode: "workspace_only",
  },
];

function cleanText(value = "") {
  return String(value ?? "").trim();
}

function numberValue(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function safeParse(raw, fallback) {
  try { return raw ? JSON.parse(raw) : fallback; } catch (_) { return fallback; }
}

function slug(value = "") {
  return cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "default";
}

function roleLabelFor(value = "") {
  const key = cleanText(value).toLowerCase();
  return RECIPIENT_ROLE_OPTIONS.find((item) => item.id === key)?.label || cleanText(value) || "Manager";
}

function uniqueList(items = []) {
  return Array.from(new Set((Array.isArray(items) ? items : []).map((item) => cleanText(item)).filter(Boolean)));
}

function validEmail(value = "") {
  const email = cleanText(value).toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
}

function normalizeWhatsappNumber(value = "") {
  const raw = cleanText(value);
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (raw.startsWith("+")) return `+${digits}`;
  if (digits.startsWith("27")) return `+${digits}`;
  if (digits.startsWith("0")) return `+27${digits.slice(1)}`;
  return `+${digits}`;
}

function siteForEvent(event = {}) {
  const target = normalizeSiteId(event.siteId || event.siteName || "");
  return loadSites().find((site) => {
    const values = [site.siteId, site.name, site.storeKey, ...(Array.isArray(site.aliases) ? site.aliases : [])];
    return values.some((value) => normalizeSiteId(value || "") === target);
  }) || null;
}

function getSiteAlertPeople(event = {}) {
  const site = siteForEvent(event);
  if (!site || site.active === false) return [];
  const people = loadProfiles().map((person) => ({ ...person, fullName: [person.name, person.surname].filter(Boolean).join(" ").trim(), role: person.operationalRole || person.role, active: person.active !== false }));
  // Stage identity is explicit. Sharing an address across roles must not
  // suppress the later role, and a missing manager must not notify Owner early.
  const manager = people.find((person) => person.id === site.siteManagerPersonId);
  const assigned = manager ? [{ ...manager, role: "Site Manager" }] : [];
  const legacy = !site.siteManagerPersonId && event.escalationPlan?.version !== 2
    ? [{ id: "legacy-site-manager", fullName: "Site Manager", role: "Site Manager", email: site.siteManagerEmail, whatsapp: site.siteManagerWhatsapp, active: true }]
    : [];

  const byId = new Map();
  [...assigned, ...legacy].forEach((person) => {
    if (!person || person.active === false) return;
    const email = validEmail(person.email);
    const whatsapp = normalizeWhatsappNumber(person.whatsapp || person.phone || person.mobile);
    if (!email && !whatsapp) return;
    const key = person.id || email || whatsapp;
    byId.set(key, { ...person, email, whatsapp });
  });
  return Array.from(byId.values());
}

function getSiteAlertEmails(event = {}) {
  return getSiteAlertPeople(event).map((person) => person.email).filter(Boolean);
}

function getSiteAlertWhatsappNumbers(event = {}) {
  return getSiteAlertPeople(event).map((person) => person.whatsapp).filter(Boolean);
}

function getNotificationRecipientsForChannel(event = {}, rule = {}, channel = "workspace") {
  if (channel === "email") return getSiteAlertEmails(event);
  if (channel === "whatsapp") return getSiteAlertWhatsappNumbers(event);
  return ["Manager"];
}

function responseSeverityLabel(value = "") {
  const key = cleanText(value).toLowerCase();
  if (key === "critical") return "Critical";
  if (key === "high") return "High";
  if (key === "medium") return "Medium";
  return "Low";
}

function formatCurrency(value) {
  const amount = numberValue(value);
  const abs = Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: Math.abs(amount) % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  return amount < 0 ? `-R${abs}` : `R${abs}`;
}

function formatQuantity(value) {
  const amount = numberValue(value);
  return amount.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 });
}

function escapeHtml(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function morpheusResponseUrl(event = {}) {
  const sessionId = cleanText(event.sessionId);
  if (!sessionId) return "";
  try {
    const currentOrigin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
    const origin = /localhost|127\.0\.0\.1/i.test(currentOrigin) ? "https://matrixos.co.za" : currentOrigin;
    return origin ? `${origin}/morpheus-count/session/${encodeURIComponent(sessionId)}` : "";
  } catch (_) {
    return "";
  }
}

function varianceSeverity(value) {
  const impact = Math.abs(numberValue(value));
  if (impact >= 1000) return { label: "Critical", color: "#b42318", background: "#fee4e2" };
  if (impact >= 500) return { label: "High", color: "#b54708", background: "#ffead5" };
  if (impact >= 100) return { label: "Medium", color: "#854a0e", background: "#fef0c7" };
  return { label: "Low", color: "#027a48", background: "#d1fadf" };
}

function productDisplayParts(value = "") {
  const parts = cleanText(value).split(/\s+-\s+/).filter(Boolean);
  return {
    name: parts[0] || "Unnamed product",
    detail: parts.slice(1).join(" • "),
  };
}

function varianceRows(event = {}) {
  return (Array.isArray(event.products) ? event.products : [])
    .filter(Boolean)
    .map((product) => {
      const severity = varianceSeverity(product.exposure);
      const display = productDisplayParts(product.product);
      const varianceValue = numberValue(product.variance);
      const exposureValue = numberValue(product.exposure);
      return `
      <div style="border-bottom:1px solid #dbe7f3;padding:14px 16px;">
        <div style="font-size:15px;font-weight:800;color:#10233b;line-height:1.35;word-break:break-word;">${escapeHtml(display.name)}</div>
        ${display.detail ? `<div style="font-size:12px;color:#60758a;margin-top:4px;line-height:1.4;word-break:break-word;">${escapeHtml(display.detail)}</div>` : ""}
        ${product.sku ? `<div style="font-size:12px;color:#60758a;margin-top:3px;">SKU ${escapeHtml(product.sku)}</div>` : ""}
        <div style="display:inline-block;margin-top:8px;padding:3px 8px;border-radius:999px;font-size:11px;font-weight:700;color:${severity.color};background:${severity.background};">${severity.label}</div>
        <table role="presentation" style="width:100%;border-collapse:collapse;margin-top:12px;table-layout:fixed;">
          <tr>
            <td style="width:50%;padding:5px 8px 5px 0;color:#60758a;font-size:12px;">System</td>
            <td style="width:50%;padding:5px 0;text-align:right;font-weight:700;color:#10233b;">${escapeHtml(formatQuantity(product.systemSoh))}</td>
          </tr>
          <tr>
            <td style="padding:5px 8px 5px 0;color:#60758a;font-size:12px;">Counted</td>
            <td style="padding:5px 0;text-align:right;font-weight:700;color:#10233b;">${escapeHtml(formatQuantity(product.counted))}</td>
          </tr>
          <tr>
            <td style="padding:5px 8px 5px 0;color:#60758a;font-size:12px;">Difference</td>
            <td style="padding:5px 0;text-align:right;font-weight:800;color:${varianceValue < 0 ? "#d9365e" : varianceValue > 0 ? "#149b68" : "#34495e"};">${varianceValue > 0 ? "+" : ""}${escapeHtml(formatQuantity(varianceValue))}</td>
          </tr>
          <tr>
            <td style="padding:5px 8px 5px 0;color:#60758a;font-size:12px;">Value</td>
            <td style="padding:5px 0;text-align:right;font-weight:800;color:${exposureValue < 0 ? "#d9365e" : exposureValue > 0 ? "#149b68" : "#34495e"};">${escapeHtml(formatCurrency(product.exposure))}</td>
          </tr>
        </table>
      </div>`;
    })
    .join("");
}

function varianceTableRows(event = {}) {
  return (Array.isArray(event.products) ? event.products : [])
    .filter(Boolean)
    .map((product) => {
      const severity = varianceSeverity(product.exposure);
      const display = productDisplayParts(product.product);
      const varianceValue = numberValue(product.variance);
      const exposureValue = numberValue(product.exposure);
      return `
        <tr>
          <td style="padding:14px 10px;border-top:1px solid #dbe7f3;vertical-align:top;">
            <div style="font-size:14px;font-weight:800;color:#10233b;line-height:1.35;">${escapeHtml(display.name)}</div>
            ${display.detail ? `<div style="font-size:12px;color:#60758a;margin-top:3px;line-height:1.35;">${escapeHtml(display.detail)}</div>` : ""}
            ${product.sku ? `<div style="font-size:12px;color:#60758a;margin-top:3px;">SKU ${escapeHtml(product.sku)}</div>` : ""}
            <div style="display:inline-block;margin-top:7px;padding:3px 8px;border-radius:999px;font-size:11px;font-weight:700;color:${severity.color};background:${severity.background};">${severity.label}</div>
          </td>
          <td style="padding:14px 8px;border-top:1px solid #dbe7f3;text-align:right;vertical-align:top;font-weight:700;color:#10233b;">${escapeHtml(formatQuantity(product.systemSoh))}</td>
          <td style="padding:14px 8px;border-top:1px solid #dbe7f3;text-align:right;vertical-align:top;font-weight:700;color:#10233b;">${escapeHtml(formatQuantity(product.counted))}</td>
          <td style="padding:14px 8px;border-top:1px solid #dbe7f3;text-align:right;vertical-align:top;font-weight:800;color:${varianceValue < 0 ? "#d9365e" : varianceValue > 0 ? "#149b68" : "#34495e"};">${varianceValue > 0 ? "+" : ""}${escapeHtml(formatQuantity(varianceValue))}</td>
          <td style="padding:14px 10px;border-top:1px solid #dbe7f3;text-align:right;vertical-align:top;font-weight:800;color:${exposureValue < 0 ? "#d9365e" : exposureValue > 0 ? "#149b68" : "#34495e"};white-space:nowrap;">${escapeHtml(formatCurrency(product.exposure))}</td>
        </tr>`;
    })
    .join("");
}

function buildEmailHtml(event = {}, details = {}) {
  const products = varianceRows(event);
  const productTableRows = varianceTableRows(event);
  const responseUrl = morpheusResponseUrl(event);
  const severity = escapeHtml(details.severity || "Inventory");
  const exposureColor = numberValue(event.exposure) < 0 ? "#d9365e" : numberValue(event.exposure) > 0 ? "#149b68" : "#34495e";
  return `
  <style>
    .mc-desktop-variance { display:block !important; }
    .mc-mobile-variance { display:none !important; }
    @media only screen and (max-width:600px) {
      .mc-desktop-variance { display:none !important; max-height:0 !important; overflow:hidden !important; }
      .mc-mobile-variance { display:block !important; }
    }
  </style>
  <div style="margin:0;background:#f3f7fb;padding:12px;font-family:Arial,Helvetica,sans-serif;color:#10233b;-webkit-text-size-adjust:100%;">
    <div style="width:100%;max-width:680px;margin:0 auto;background:#ffffff;border:1px solid #d9e6f2;border-radius:16px;overflow:hidden;box-sizing:border-box;">
      <div style="background:#071a2e;padding:22px 20px;color:#ffffff;">
        <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#39d4ff;font-weight:700;">Morpheus by MatrixOS</div>
        <h1 style="font-size:24px;line-height:1.25;margin:10px 0 4px;word-break:break-word;">${severity} Inventory Variance</h1>
        <div style="font-size:15px;color:#b9d0e5;word-break:break-word;">${escapeHtml(details.site)} · ${escapeHtml(details.section)} · ${escapeHtml(details.categoryScope || "All Count Groups")}</div>
      </div>
      <div style="padding:22px 20px;box-sizing:border-box;">
        <div style="background:#f6f9fc;border:1px solid #dbe7f3;border-radius:12px;padding:16px;margin-bottom:12px;box-sizing:border-box;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#60758a;">Financial exposure</div>
          <div style="font-size:30px;font-weight:800;margin-top:6px;color:${exposureColor};word-break:break-word;">${escapeHtml(details.exposure)}</div>
        </div>
        <div style="background:#f6f9fc;border:1px solid #dbe7f3;border-radius:12px;padding:16px;margin-bottom:22px;box-sizing:border-box;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#60758a;">Products requiring review</div>
          <div style="font-size:30px;font-weight:800;margin-top:6px;">${escapeHtml(details.flagged)}</div>
        </div>

        <table role="presentation" style="width:100%;border-collapse:collapse;margin-bottom:22px;table-layout:fixed;">
          <tr><td style="padding:6px 8px 6px 0;color:#60758a;width:42%;vertical-align:top;">Site</td><td style="padding:6px 0;font-weight:700;word-break:break-word;">${escapeHtml(details.site)}</td></tr>
          <tr><td style="padding:6px 8px 6px 0;color:#60758a;vertical-align:top;">Section</td><td style="padding:6px 0;font-weight:700;word-break:break-word;">${escapeHtml(details.section)}</td></tr>
          ${details.cycle ? `<tr><td style="padding:6px 8px 6px 0;color:#60758a;vertical-align:top;">Cycle</td><td style="padding:6px 0;font-weight:700;word-break:break-word;">${escapeHtml(details.cycle)}</td></tr>` : ""}
          <tr><td style="padding:6px 8px 6px 0;color:#60758a;vertical-align:top;">Count groups</td><td style="padding:6px 0;font-weight:700;word-break:break-word;">${escapeHtml(details.categoryScope || "All Count Groups")}</td></tr>
          <tr><td style="padding:6px 8px 6px 0;color:#60758a;vertical-align:top;">Count completed by</td><td style="padding:6px 0;font-weight:700;word-break:break-word;">${escapeHtml(details.startedBy)}</td></tr>
        </table>

        ${products ? `
          <div style="margin-top:8px;">
            <div style="font-size:13px;letter-spacing:1px;text-transform:uppercase;color:#006d91;font-weight:800;margin-bottom:12px;">Inventory variance</div>
            <div class="mc-desktop-variance" style="border:1px solid #dbe7f3;border-radius:12px;overflow:hidden;box-sizing:border-box;">
              <table role="presentation" style="width:100%;border-collapse:collapse;table-layout:fixed;">
                <thead>
                  <tr style="background:#f6f9fc;">
                    <th style="width:40%;padding:11px 10px;text-align:left;color:#60758a;font-size:11px;letter-spacing:.5px;text-transform:uppercase;">Product</th>
                    <th style="width:15%;padding:11px 8px;text-align:right;color:#60758a;font-size:11px;letter-spacing:.5px;text-transform:uppercase;">System</th>
                    <th style="width:15%;padding:11px 8px;text-align:right;color:#60758a;font-size:11px;letter-spacing:.5px;text-transform:uppercase;">Counted</th>
                    <th style="width:15%;padding:11px 8px;text-align:right;color:#60758a;font-size:11px;letter-spacing:.5px;text-transform:uppercase;">Difference</th>
                    <th style="width:15%;padding:11px 10px;text-align:right;color:#60758a;font-size:11px;letter-spacing:.5px;text-transform:uppercase;">Value</th>
                  </tr>
                </thead>
                <tbody>${productTableRows}</tbody>
              </table>
            </div>
            <div class="mc-mobile-variance" style="display:none;border:1px solid #dbe7f3;border-radius:12px;overflow:hidden;box-sizing:border-box;">${products}</div>
          </div>` : ""}

        <p style="margin:22px 0 8px;line-height:1.6;color:#34495e;font-weight:700;">Morpheus has completed this inventory count and detected inventory variances that require review.</p>
        <p style="margin:0 0 18px;line-height:1.6;color:#60758a;">This notification was sent immediately so the stock can be investigated while the activity is still recent.</p>
        ${responseUrl ? `<a href="${escapeHtml(responseUrl)}" style="display:block;background:#19bff2;color:#001524;text-decoration:none;font-weight:800;border-radius:10px;padding:14px 16px;text-align:center;box-sizing:border-box;word-break:break-word;">Review Count in Morpheus</a>` : ""}
      </div>
      <div style="padding:18px 20px;text-align:center;background:#f8fbfe;border-top:1px solid #e3edf6;color:#7890a5;font-size:12px;line-height:1.6;">
        This alert was generated automatically by Morpheus.<br />
        <strong style="color:#49647c;">Operate with Clarity.</strong>
      </div>
    </div>
  </div>`;
}

export function loadNotificationRules() {
  const parsed = safeParse(mcGetItem(MC_NOTIFICATION_RULES_KEY), null);
  const rules = Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_RULES;
  return rules.filter(Boolean).map((rule) => ({
    active: true,
    trigger: "severity",
    severities: [],
    minExposure: 0,
    minFlaggedProducts: 0,
    recipients: ["Manager"],
    channels: ["workspace"],
    deliveryMode: "immediate",
    ...rule,
  }));
}

export function saveNotificationRules(rules = []) {
  const cleanRules = Array.isArray(rules) ? rules.filter(Boolean) : [];
  const value = JSON.stringify(cleanRules);
  try { mcSetItem(MC_NOTIFICATION_RULES_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_NOTIFICATION_RULES_KEY, value).catch(() => {}); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { rules: cleanRules } })); } catch (_) {}
  return cleanRules;
}

export function loadResponseNotifications() {
  const parsed = safeParse(mcGetItem(MC_NOTIFICATIONS_KEY), []);
  return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
}

export function saveResponseNotifications(notifications = []) {
  const cleanNotifications = Array.isArray(notifications) ? notifications.filter(Boolean).slice(0, MAX_NOTIFICATIONS) : [];
  const value = JSON.stringify(cleanNotifications);
  try { mcSetItem(MC_NOTIFICATIONS_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_NOTIFICATIONS_KEY, value).catch(() => {}); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { notifications: cleanNotifications } })); } catch (_) {}
  return cleanNotifications;
}

export function buildNotificationMessage(event = {}, channel = "workspace") {
  const severity = responseSeverityLabel(event.severity || "low");
  const site = cleanText(event.siteName || "Unknown site");
  const section = cleanText(event.sectionName || "Unassigned");
  const cycle = cleanText(event.cycleName || "");
  const categoryScope = cleanText(event.categoryScopeLabel || (Array.isArray(event.categoryNames) && event.categoryNames.length ? event.categoryNames.join(" • ") : "All Count Groups"));
  const exposure = formatCurrency(event.exposure);
  const flagged = Math.abs(numberValue(event.flaggedProducts));
  const startedBy = cleanText(event.countedBy || event.startedBy || "Unknown user");
  const subject = `${severity} Inventory Variance · ${site} · ${exposure}`;

  if (channel === "whatsapp") {
    return {
      subject,
      body: `⚠ Morpheus Alert\n\n${site}\n${cycle ? `${cycle}\n` : ""}Section: ${section}\nCount groups: ${categoryScope}\n\n${severity} variance detected.\nExposure: ${exposure}\n${flagged} flagged product${flagged === 1 ? "" : "s"}.\nCount completed by: ${startedBy}\n\nReview count: ${morpheusResponseUrl(event) || "Open Morpheus to investigate."}`,
    };
  }

  if (channel === "email") {
    return {
      subject,
      body: `${severity} Inventory Variance\n\nSite: ${site}${cycle ? `\nCycle: ${cycle}` : ""}\nSection: ${section}\nCount groups: ${categoryScope}\nExposure: ${exposure}\nProducts flagged: ${flagged}\nCount completed by: ${startedBy}\n\nMorpheus detected this variance when the count was completed. Review the response using the link below.\n\nOpen Morpheus Count: ${morpheusResponseUrl(event)}`,
      html: buildEmailHtml(event, { severity, site, section, cycle, categoryScope, exposure, flagged, startedBy }),
    };
  }

  return {
    subject,
    body: `${severity} variance · ${site} · ${section} · ${categoryScope} · ${exposure} · ${flagged} flagged`,
  };
}

export function ruleMatchesEvent(rule = {}, event = {}) {
  if (rule.active === false) return false;
  const severity = cleanText(event.severity || "low").toLowerCase();
  const severities = Array.isArray(rule.severities) ? rule.severities.map((item) => cleanText(item).toLowerCase()).filter(Boolean) : [];
  if (severities.length && !severities.includes(severity)) return false;
  if (Math.abs(numberValue(event.exposure)) < Math.abs(numberValue(rule.minExposure))) return false;
  if (Math.abs(numberValue(event.flaggedProducts)) < Math.abs(numberValue(rule.minFlaggedProducts))) return false;
  return true;
}

export function buildNotificationsForEvent(event = {}) {
  if (!event?.id) return [];

  // P4.4 — Alerts are a projection of the frozen canonical completion only.
  // Response corrections, investigations and other downstream mutations must
  // never manufacture a new inventory alert or change the facts in one.
  if (cleanText(event.source) !== "canonical_count_ledger") return [];
  if (!cleanText(event.sourceCompletedAt)) return [];
  const frozen = event.sourceSessionSummary && typeof event.sourceSessionSummary === "object"
    ? event.sourceSessionSummary
    : null;
  if (!frozen) return [];
  const frozenExposure = Number(frozen.exposure);
  const frozenFlags = Number(frozen.flaggedProducts);
  if (!Number.isFinite(frozenExposure) || !Number.isFinite(frozenFlags)) return [];

  const alertEvent = {
    ...event,
    exposure: frozenExposure,
    flaggedProducts: Math.max(0, frozenFlags),
  };
  const notifications = [];
  const firstStage = event.escalationPlan?.version === 2 ? event.escalationPlan.stages?.find((stage) => stage.roleKey === "siteManager") : null;
  const channels = firstStage
    ? ["workspace", ...(Number(firstStage.delayMinutes) === 0 ? firstStage.channels || [] : [])]
    : ["workspace", "email", "whatsapp"];

  channels.forEach((cleanChannel) => {
    const recipients = getNotificationRecipientsForChannel(alertEvent, {}, cleanChannel);
    recipients.forEach((recipient) => {
      const message = buildNotificationMessage(alertEvent, cleanChannel);
      const person = cleanChannel === "email"
        ? getSiteAlertPeople(alertEvent).find((item) => item.email === recipient)
        : cleanChannel === "whatsapp"
          ? getSiteAlertPeople(alertEvent).find((item) => item.whatsapp === recipient)
          : null;
      notifications.push({
        id: `notification:${event.id}:response-designed:${slug(recipient)}:${cleanChannel}`,
        eventId: event.id,
        ruleId: "response-designed",
        ruleName: "Automatic variance response",
        recipient: cleanText(recipient) || "Manager",
        recipientName: person?.fullName || cleanText(recipient) || "Manager",
        recipientRole: person?.role || (cleanChannel === "workspace" ? "Manager" : ""),
        channel: cleanChannel,
        severity: alertEvent.severity || "low",
        siteId: alertEvent.siteId || "",
        siteName: alertEvent.siteName || "Unknown site",
        sectionId: alertEvent.sectionId || "",
        sectionName: alertEvent.sectionName || "Unassigned",
        categoryScopeType: alertEvent.categoryScopeType || "all",
        categoryNames: Array.isArray(alertEvent.categoryNames) ? alertEvent.categoryNames : [],
        countGroupNames: Array.isArray(alertEvent.countGroupNames) ? alertEvent.countGroupNames : (Array.isArray(alertEvent.categoryNames) ? alertEvent.categoryNames : []),
        categoryScopeLabel: alertEvent.categoryScopeLabel || "All Count Groups",
        exposure: numberValue(alertEvent.exposure),
        flaggedProducts: numberValue(alertEvent.flaggedProducts),
        subject: message.subject,
        message: message.body,
        messageHtml: message.html || "",
        responseEvent: alertEvent,
        source: "canonical_count_ledger",
        sourceRevision: Number(alertEvent.sourceRevision || 0),
        sourceCompletedAt: alertEvent.sourceCompletedAt,
        sourceSessionSummary: { ...frozen, exposure: frozenExposure, flaggedProducts: Math.max(0, frozenFlags) },
        status: cleanChannel === "workspace" ? "delivered" : "queued",
        createdAt: new Date().toISOString(),
        queuedAt: new Date().toISOString(),
        sentAt: cleanChannel === "workspace" ? new Date().toISOString() : null,
        deliveredAt: cleanChannel === "workspace" ? new Date().toISOString() : null,
        readAt: null,
        retries: 0,
      });
    });
  });

  if (channels.includes("whatsapp") && !notifications.some((item) => item.channel === "whatsapp")) {
    const fallbackPerson = getSiteAlertPeople(alertEvent)[0] || null;
    const now = new Date().toISOString();
    notifications.push({
      id: `notification:${event.id}:response-designed:missing-whatsapp:whatsapp`,
      eventId: event.id,
      ruleId: "response-designed",
      ruleName: "Automatic variance response",
      recipient: "",
      recipientName: fallbackPerson?.fullName || "Configured site recipient",
      recipientRole: fallbackPerson?.role || "Recipient",
      channel: "whatsapp",
      severity: alertEvent.severity || "low",
      siteId: alertEvent.siteId || "",
      siteName: alertEvent.siteName || "Unknown site",
      sectionId: alertEvent.sectionId || "",
      sectionName: alertEvent.sectionName || "Unassigned",
      subject: "WhatsApp delivery skipped",
      message: "No WhatsApp number is configured for the selected site recipient.",
      responseEvent: alertEvent,
        source: "canonical_count_ledger",
        sourceRevision: Number(alertEvent.sourceRevision || 0),
        sourceCompletedAt: alertEvent.sourceCompletedAt,
        sourceSessionSummary: { ...frozen, exposure: frozenExposure, flaggedProducts: Math.max(0, frozenFlags) },
      status: "skipped",
      skipReason: "no_whatsapp_number",
      createdAt: now,
      queuedAt: null,
      sentAt: null,
      deliveredAt: null,
      readAt: null,
      retries: 0,
    });
  }
  return notifications;
}

export function enqueueNotificationsForEvent(event = {}, rules = loadNotificationRules()) {
  const newNotifications = buildNotificationsForEvent(event, rules);
  if (!newNotifications.length) return loadResponseNotifications();
  const existing = loadResponseNotifications();
  const existingIds = new Set(existing.map((item) => item?.id).filter(Boolean));
  const uniqueNew = newNotifications.filter((item) => !existingIds.has(item.id));
  if (!uniqueNew.length) return existing;
  return saveResponseNotifications([...uniqueNew, ...existing].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()));
}

export function buildNotificationSummary(notifications = []) {
  const safe = Array.isArray(notifications) ? notifications.filter(Boolean) : [];
  return {
    all: safe,
    queued: safe.filter((item) => item.status === "queued"),
    sent: safe.filter((item) => item.status === "sent"),
    delivered: safe.filter((item) => item.status === "delivered"),
    skipped: safe.filter((item) => item.status === "skipped"),
    failed: safe.filter((item) => item.status === "failed"),
    sending: safe.filter((item) => item.status === "sending"),
    read: safe.filter((item) => item.status === "read"),
    prepared: safe.filter((item) => item.status === "prepared"),
    waitingDelivery: safe.filter((item) => ["queued", "sending", "failed"].includes(cleanText(item.status).toLowerCase())),
    workspace: safe.filter((item) => item.channel === "workspace"),
    email: safe.filter((item) => item.channel === "email"),
    whatsapp: safe.filter((item) => item.channel === "whatsapp"),
  };
}


export function notificationStatusLabel(value = "") {
  const key = cleanText(value || "queued").toLowerCase();
  if (key === "sending") return "Sending";
  if (key === "sent") return "Sent";
  if (key === "delivered") return "Delivered";
  if (key === "skipped") return "Skipped";
  if (key === "failed") return "Failed";
  if (key === "read") return "Read";
  if (key === "prepared") return "Prepared";
  return "Queued";
}

export function updateNotificationStatus(notificationId = "", status = "queued", actor = "System", details = {}) {
  const cleanId = cleanText(notificationId);
  if (!cleanId) return loadResponseNotifications();
  const allowed = new Set(["queued", "sending", "prepared", "sent", "delivered", "skipped", "failed", "read"]);
  const cleanStatus = allowed.has(cleanText(status).toLowerCase()) ? cleanText(status).toLowerCase() : "queued";
  const now = new Date().toISOString();
  const next = loadResponseNotifications().map((item) => {
    if (item?.id !== cleanId) return item;
    const timeline = Array.isArray(item.timeline) ? item.timeline : [];
    return {
      ...item,
      status: cleanStatus,
      updatedAt: now,
      sendingAt: cleanStatus === "sending" ? (item.sendingAt || now) : item.sendingAt,
      preparedAt: cleanStatus === "prepared" ? (item.preparedAt || now) : item.preparedAt,
      sentAt: ["sent", "delivered", "read"].includes(cleanStatus) ? (item.sentAt || now) : item.sentAt,
      deliveredAt: ["delivered", "read"].includes(cleanStatus) ? (item.deliveredAt || now) : item.deliveredAt,
      readAt: cleanStatus === "read" ? (item.readAt || now) : item.readAt,
      failedAt: cleanStatus === "failed" ? now : item.failedAt,
      lastUpdatedBy: cleanText(actor || "System"),
      ...(details && typeof details === "object" ? details : {}),
      timeline: [
        ...timeline,
        {
          id: `notification-timeline:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
          timestamp: now,
          action: `Status changed to ${notificationStatusLabel(cleanStatus)}`,
          by: cleanText(actor || "System"),
        },
      ],
    };
  });
  return saveResponseNotifications(next);
}

export function retryNotification(notificationId = "", actor = "System") {
  const cleanId = cleanText(notificationId);
  if (!cleanId) return loadResponseNotifications();
  const now = new Date().toISOString();
  const next = loadResponseNotifications().map((item) => {
    if (item?.id !== cleanId) return item;
    const timeline = Array.isArray(item.timeline) ? item.timeline : [];
    return {
      ...item,
      status: "queued",
      queuedAt: now,
      failedAt: null,
      preparedAt: null,
      updatedAt: now,
      retries: numberValue(item.retries) + 1,
      lastUpdatedBy: cleanText(actor || "System"),
      timeline: [
        ...timeline,
        {
          id: `notification-timeline:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
          timestamp: now,
          action: "Retry queued",
          by: cleanText(actor || "System"),
        },
      ],
    };
  });
  return saveResponseNotifications(next);
}

export function getNotificationsForEvent(eventId = "") {
  const cleanId = cleanText(eventId);
  return loadResponseNotifications().filter((item) => item?.eventId === cleanId);
}


export const MC_EMAIL_DELIVERY_CONFIG_KEY = "mc-email-delivery-config";
export const MC_EMAIL_DELIVERY_EVENT = "mc-email-delivery-event";

const DEFAULT_EMAIL_DELIVERY_CONFIG = {
  enabled: true,
  testMode: false,
  endpointUrl: "https://us-central1-invoice-app-v2-9aee2.cloudfunctions.net/sendMorpheusEmail",
  provider: "custom_webhook",
  fromName: "Morpheus",
  fromEmail: "alerts@matrixos.co.za",
  replyTo: "",
};

export function loadEmailDeliveryConfig() {
  const parsed = safeParse(mcGetItem(MC_EMAIL_DELIVERY_CONFIG_KEY), null);
  const merged = { ...DEFAULT_EMAIL_DELIVERY_CONFIG, ...(parsed && typeof parsed === "object" ? parsed : {}) };

  // Response Designed must use the real deployed channel. Older builds saved
  // testMode=true and then marked alerts as delivered without calling the
  // provider, which made the UI claim success while no email was sent.
  return {
    ...merged,
    enabled: true,
    testMode: false,
    endpointUrl: DEFAULT_EMAIL_DELIVERY_CONFIG.endpointUrl,
  };
}

export function saveEmailDeliveryConfig(config = {}) {
  const cleanConfig = { ...DEFAULT_EMAIL_DELIVERY_CONFIG, ...(config && typeof config === "object" ? config : {}) };
  const value = JSON.stringify(cleanConfig);
  try { mcSetItem(MC_EMAIL_DELIVERY_CONFIG_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_EMAIL_DELIVERY_CONFIG_KEY, value).catch(() => {}); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent(MC_EMAIL_DELIVERY_EVENT, { detail: { config: cleanConfig } })); } catch (_) {}
  return cleanConfig;
}

export function buildEmailDeliveryPayload(notification = {}, config = loadEmailDeliveryConfig()) {
  return {
    provider: config.provider || "custom_webhook",
    notificationId: notification.id,
    eventId: notification.eventId,
    to: notification.recipient,
    from: { name: config.fromName || "Morpheus", email: config.fromEmail || "alerts@matrixos.co.za" },
    replyTo: config.replyTo || config.fromEmail || "",
    subject: notification.subject || "Morpheus inventory alert",
    text: notification.message || "",
    html: notification.messageHtml || String(notification.message || "").split("\n").map((line) => `<p>${escapeHtml(line) || "&nbsp;"}</p>`).join(""),
    meta: { source: "morpheus", channel: "email", createdAt: notification.createdAt || new Date().toISOString() },
  };
}

export async function deliverEmailNotification(notification = {}, config = loadEmailDeliveryConfig(), actor = "Morpheus") {
  if (!notification?.id || notification.channel !== "email") return { ok: false, reason: "not_email" };
  updateNotificationStatus(notification.id, "sending", actor);

  if (config.testMode) {
    updateNotificationStatus(notification.id, "delivered", `${actor} · test email mode`);
    return {
      ok: true,
      mode: "test",
      recipient: notification.recipient,
      recipientName: notification.recipientName || notification.recipient,
      recipientRole: notification.recipientRole || "Recipient",
    };
  }

  if (!config.enabled || !cleanText(config.endpointUrl)) {
    updateNotificationStatus(notification.id, "failed", `${actor} · email not configured`);
    return { ok: false, reason: "not_configured" };
  }

  try {
    const response = await fetch(config.endpointUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildEmailDeliveryPayload(notification, config)),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.ok === false) throw new Error(result?.error || `Email endpoint returned ${response.status}`);
    updateNotificationStatus(notification.id, "delivered", `${actor} · provider confirmed`);
    return {
      ok: true,
      status: response.status,
      providerMessageId: result?.messageId || "",
      recipient: notification.recipient,
      recipientName: notification.recipientName || notification.recipient,
      recipientRole: notification.recipientRole || "Recipient",
    };
  } catch (error) {
    updateNotificationStatus(notification.id, "failed", `${actor} · ${error?.message || "email failed"}`);
    return { ok: false, reason: error?.message || "email_failed" };
  }
}

export async function processQueuedEmailNotifications(config = loadEmailDeliveryConfig(), actor = "Morpheus") {
  const queued = loadResponseNotifications().filter((item) => item?.channel === "email" && cleanText(item.status).toLowerCase() === "queued");
  const results = [];
  for (const notification of queued) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await deliverEmailNotification(notification, config, actor));
  }
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { emailDelivery: results } })); } catch (_) {}
  return results;
}


export const MC_WHATSAPP_DELIVERY_CONFIG_KEY = "mc-whatsapp-delivery-config";
export const MC_WHATSAPP_DELIVERY_EVENT = "mc-whatsapp-delivery-event";

const DEFAULT_WHATSAPP_DELIVERY_CONFIG = {
  enabled: true,
  testMode: false,
  provider: "matrixos_whatsapp_gateway",
  endpointUrl: "https://us-central1-invoice-app-v2-9aee2.cloudfunctions.net/sendMorpheusWhatsApp",
  defaultSender: "MatrixOS",
  templateName: "morpheus_inventory_alert",
  templateLanguage: "en",
  // Production routing: deliver to the WhatsApp number resolved from People / Directory
  // and selected for the site. No sandbox recipient override is permitted.
  sandboxMode: false,
  sandboxRecipient: "",
};

export function loadWhatsappDeliveryConfig() {
  const parsed = safeParse(mcGetItem(MC_WHATSAPP_DELIVERY_CONFIG_KEY), null);
  return {
    ...DEFAULT_WHATSAPP_DELIVERY_CONFIG,
    ...(parsed && typeof parsed === "object" ? parsed : {}),
    enabled: true,
    endpointUrl: DEFAULT_WHATSAPP_DELIVERY_CONFIG.endpointUrl,
    // Production routing is enforced so stale browser settings cannot redirect
    // notifications to the retired Meta sandbox recipient.
    sandboxMode: false,
    sandboxRecipient: "",
  };
}

export function saveWhatsappDeliveryConfig(config = {}) {
  const cleanConfig = { ...DEFAULT_WHATSAPP_DELIVERY_CONFIG, ...(config && typeof config === "object" ? config : {}) };
  const value = JSON.stringify(cleanConfig);
  try { mcSetItem(MC_WHATSAPP_DELIVERY_CONFIG_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_WHATSAPP_DELIVERY_CONFIG_KEY, value).catch(() => {}); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent(MC_WHATSAPP_DELIVERY_EVENT, { detail: { config: cleanConfig } })); } catch (_) {}
  return cleanConfig;
}

function whatsappTemplateParameters(notification = {}) {
  const event = notification.responseEvent || {};
  const site = cleanText(event.siteName || notification.siteName || "Unknown site");
  const section = cleanText(event.sectionName || notification.sectionName || "Unassigned");
  const countGroups = Array.isArray(event.countGroupNames) && event.countGroupNames.length
    ? event.countGroupNames.join(", ")
    : (Array.isArray(notification.countGroupNames) && notification.countGroupNames.length
      ? notification.countGroupNames.join(", ")
      : cleanText(event.categoryScopeLabel || notification.categoryScopeLabel || "All Count Groups"));
  const severity = responseSeverityLabel(event.severity || notification.severity || "low");
  const exposure = formatCurrency(event.exposure ?? notification.exposure);
  const flaggedProducts = String(Math.abs(numberValue(event.flaggedProducts ?? notification.flaggedProducts)));
  const completedBy = cleanText(event.countedBy || event.completedBy || event.startedBy || "Unknown user");
  const reviewUrl = morpheusResponseUrl(event) || "https://matrixos.co.za/morpheus-count/workspace";

  // Exact Meta template order for morpheus_inventory_alert:
  // site, section, count group, severity, exposure, flagged products, completed by, review link.
  return [site, section, countGroups, severity, exposure, flaggedProducts, completedBy, reviewUrl];
}

export function buildWhatsappDeliveryPayload(notification = {}, config = loadWhatsappDeliveryConfig()) {
  return {
    provider: config.provider || "matrixos_whatsapp_gateway",
    notificationId: notification.id,
    eventId: notification.eventId,
    to: normalizeWhatsappNumber(config.sandboxMode && config.sandboxRecipient ? config.sandboxRecipient : notification.recipient),
    intendedRecipient: normalizeWhatsappNumber(notification.recipient),
    sandboxMode: config.sandboxMode === true,
    recipientName: notification.recipientName || notification.recipient,
    recipientRole: notification.recipientRole || "Recipient",
    from: config.defaultSender || "MatrixOS",
    subject: notification.subject || "Morpheus inventory alert",
    text: notification.message || notification.subject || "Morpheus inventory alert",
    template: config.templateName || "morpheus_inventory_alert",
    language: config.templateLanguage || "en_US",
    parameters: whatsappTemplateParameters(notification),
    responseUrl: notification?.responseEvent ? morpheusResponseUrl(notification.responseEvent) : "",
    meta: { source: "morpheus", channel: "whatsapp", createdAt: notification.createdAt || new Date().toISOString() },
  };
}

export async function deliverWhatsappNotification(notification = {}, config = loadWhatsappDeliveryConfig(), actor = "Morpheus") {
  if (!notification?.id || notification.channel !== "whatsapp") return { ok: false, reason: "not_whatsapp" };
  const recipient = normalizeWhatsappNumber(notification.recipient);
  if (!recipient) return { ok: false, reason: "invalid_recipient" };
  updateNotificationStatus(notification.id, "sending", actor);

  if (!config.enabled || !cleanText(config.endpointUrl)) {
    updateNotificationStatus(notification.id, "failed", `${actor} · WhatsApp gateway not configured`);
    return { ok: false, reason: "not_configured" };
  }

  try {
    const response = await fetch(config.endpointUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildWhatsappDeliveryPayload(notification, config)),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.ok === false) {
      const rawError = cleanText(result?.error || result?.message || `WhatsApp gateway returned ${response.status}`);
      const codeMatch = rawError.match(/\(#?(\d+)\)/) || rawError.match(/code[\s:=]+(\d+)/i);
      const providerCode = cleanText(result?.code || result?.errorCode || codeMatch?.[2] || codeMatch?.[1]);
      const providerMessage = rawError || "WhatsApp delivery failed";
      const deliveryPayload = buildWhatsappDeliveryPayload(notification, config);
      updateNotificationStatus(notification.id, "failed", `${actor} · ${providerMessage}`, {
        providerErrorCode: providerCode,
        providerErrorMessage: providerMessage,
        providerHttpStatus: response.status,
        providerResponse: result && typeof result === "object" ? result : {},
        intendedRecipient: normalizeWhatsappNumber(notification.recipient),
        deliveredTo: normalizeWhatsappNumber(deliveryPayload.to),
        sandboxMode: deliveryPayload.sandboxMode === true,
      });
      return { ok: false, reason: providerMessage, code: providerCode, status: response.status };
    }

    if (result?.mode === "mock" || result?.sent === false) {
      updateNotificationStatus(notification.id, "prepared", `${actor} · WhatsApp prepared; Meta credentials pending`);
      return {
        ok: true,
        prepared: true,
        mode: "mock",
        recipient,
        recipientName: notification.recipientName || recipient,
        recipientRole: notification.recipientRole || "Recipient",
      };
    }

    const deliveryPayload = buildWhatsappDeliveryPayload(notification, config);
    const routedRecipient = normalizeWhatsappNumber(deliveryPayload.to);
    const sandboxDetail = deliveryPayload.sandboxMode
      ? `${actor} · Meta accepted · Sandbox routed to ${routedRecipient}`
      : (result?.duplicate ? `${actor} · duplicate prevented` : `${actor} · Meta accepted`);
    updateNotificationStatus(notification.id, "sent", sandboxDetail);
    return {
      ok: true,
      sent: true,
      duplicate: result?.duplicate === true,
      status: response.status,
      providerMessageId: result?.messageId || "",
      recipient,
      intendedRecipient: recipient,
      deliveredTo: routedRecipient,
      sandboxMode: deliveryPayload.sandboxMode === true,
      recipientName: notification.recipientName || recipient,
      recipientRole: notification.recipientRole || "Recipient",
    };
  } catch (error) {
    const providerMessage = cleanText(error?.message || "WhatsApp failed");
    const codeMatch = providerMessage.match(/\(#?(\d+)\)/) || providerMessage.match(/code[\s:=]+(\d+)/i);
    const deliveryPayload = buildWhatsappDeliveryPayload(notification, config);
    updateNotificationStatus(notification.id, "failed", `${actor} · ${providerMessage}`, {
      providerErrorCode: cleanText(codeMatch?.[2] || codeMatch?.[1]),
      providerErrorMessage: providerMessage,
      providerHttpStatus: null,
      intendedRecipient: normalizeWhatsappNumber(notification.recipient),
      deliveredTo: normalizeWhatsappNumber(deliveryPayload.to),
      sandboxMode: deliveryPayload.sandboxMode === true,
    });
    return { ok: false, reason: providerMessage };
  }
}

export async function processQueuedWhatsappNotifications(config = loadWhatsappDeliveryConfig(), actor = "Morpheus") {
  const queued = loadResponseNotifications().filter((item) => item?.channel === "whatsapp" && cleanText(item.status).toLowerCase() === "queued");
  const results = [];
  for (const notification of queued) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await deliverWhatsappNotification(notification, config, actor));
  }
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { whatsappDelivery: results } })); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent(MC_WHATSAPP_DELIVERY_EVENT, { detail: { results } })); } catch (_) {}
  return results;
}


export async function processNotificationsForEvent(eventId = "", actor = "Response Designed") {
  const cleanEventId = cleanText(eventId);
  if (!cleanEventId) return { email: [], whatsapp: [], processed: 0 };
  const queued = loadResponseNotifications().filter((item) => item?.eventId === cleanEventId && cleanText(item.status).toLowerCase() === "queued");
  const emailResults = [];
  const whatsappResults = [];
  for (const notification of queued) {
    if (notification.channel === "email") {
      // eslint-disable-next-line no-await-in-loop
      emailResults.push(await deliverEmailNotification(notification, loadEmailDeliveryConfig(), actor));
    } else if (notification.channel === "whatsapp") {
      // eslint-disable-next-line no-await-in-loop
      whatsappResults.push(await deliverWhatsappNotification(notification, loadWhatsappDeliveryConfig(), actor));
    }
  }
  const results = { email: emailResults, whatsapp: whatsappResults, processed: emailResults.length + whatsappResults.length };
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { automaticDelivery: results, eventId: cleanEventId } })); } catch (_) {}
  return results;
}

export async function processDeliveryQueue(actor = "Delivery Intelligence") {
  const emailResults = await processQueuedEmailNotifications(loadEmailDeliveryConfig(), actor);
  const whatsappResults = await processQueuedWhatsappNotifications(loadWhatsappDeliveryConfig(), actor);
  const results = { email: emailResults, whatsapp: whatsappResults, processed: emailResults.length + whatsappResults.length };
  try { window.dispatchEvent(new CustomEvent(MC_NOTIFICATION_EVENT, { detail: { deliveryQueue: results } })); } catch (_) {}
  return results;
}

export function getNotificationTimeline(notification = {}) {
  const timeline = [];
  if (notification.createdAt) timeline.push({ status: "Generated", at: notification.createdAt });
  if (notification.queuedAt) timeline.push({ status: "Queued", at: notification.queuedAt });
  if (notification.sendingAt) timeline.push({ status: "Sending", at: notification.sendingAt });
  if (notification.preparedAt) timeline.push({ status: "Prepared", at: notification.preparedAt });
  if (notification.sentAt) timeline.push({ status: "Sent", at: notification.sentAt });
  if (notification.deliveredAt) timeline.push({ status: "Delivered", at: notification.deliveredAt });
  if (notification.readAt) timeline.push({ status: "Read", at: notification.readAt });
  if (notification.failedAt) timeline.push({ status: "Failed", at: notification.failedAt });
  return timeline;
}

export function buildNotificationDetail(notification = {}, event = {}) {
  return {
    ...notification,
    preview: {
      title: notification.subject || "Inventory Variance",
      body: notification.message || "",
    },
    timeline: getNotificationTimeline(notification),
    responseEvent: event?.id || notification.eventId || null,
  };
}
